/**
 * useRedactionStreamingState — **l'affichage d'une rédaction**, monté sans
 * condition par `CanvasResidence`.
 *
 * Depuis #857 ce hook ne lit plus le réseau : il **regarde** l'écoute que
 * `redactionStreamHub` tient hors de React (voir le docblock de ce service pour
 * le pourquoi). Il ne reste ici que ce qui est réellement de l'affichage :
 *  - projeter le travail lié à la conversation du porteur ;
 *  - ouvrir le canvas au premier bloc non vide — l'instant où il y a enfin
 *    quelque chose à voir, et pas avant (chemin d'ouverture 2/5, #860) ;
 *  - refléter le raisonnement vers la carte de progression du chat, qui vit
 *    dans un autre arbre React.
 *
 * Il reste monté **sans condition**, canvas fermé compris : c'est lui qui
 * détecte le premier bloc et ouvre le canvas. Le rattachement, lui, ne dépend
 * plus de ce montage — l'écoute survit même si ce hook disparaissait.
 */

import * as React from 'react';
import { type StructuredBlock } from '@hector/shared-types';
import { useRedactionThinkingStore } from '../stores/redactionThinkingStore';
import { openCanvas } from '../state/canvasOpening';
import { redactionStreamHub, type RedactionEcoute } from './redactionStreamHub';

export type RedactionPhase = 'idle' | 'streaming' | 'done';
export type RedactionActiveKind = 'research' | 'generation' | null;

/**
 * Ce qu'un affichage de rédaction a besoin de savoir — et **rien de plus**.
 *
 * ⚠️ **Aucune identité documentaire n'est republiée ici** (#1336). Ce type
 * portait `documentSessionId`, obtenu en traduisant la conversation que
 * l'appelant venait de passer en argument. Depuis que la conversation EST
 * l'identité du document logique, republier ce champ reviendrait à rendre à
 * l'appelant sa propre entrée sous un autre nom — l'alias que #1336 interdit.
 * Un `mountEpoch` serait pire qu'un alias : une écoute vit **hors de React** et
 * survit aux remontages (#857), donc l'attacher à une époque de montage la
 * ferait taire au premier remontage légitime. Un consommateur qui a besoin
 * d'adresser le document adresse la conversation qu'il tient déjà.
 */
export interface RedactionStreamingState {
  blocks: readonly StructuredBlock[];
  phase: RedactionPhase;
  activeKind: RedactionActiveKind;
  headerLabel: string;
  /**
   * Le travail dont proviennent `blocks`. Consommé par la **garde d'époque de
   * job** du point d'écriture (#937) : elle refuse un job périmé, pas un canvas
   * hydraté. `null` quand aucun travail n'est affiché.
   */
  jobId: string | null;
  /** Nom validé au lancement, avant toute proposition du modèle. */
  documentTitle: string | null;
}

export function computeHeaderLabel(phase: RedactionPhase, activeKind: RedactionActiveKind): string {
  // Un NOM de document, pas une invitation (2026-08-13). L'ancien libellé
  // — « Page blanche — commence à écrire ou demande à Hector… » — occupait la
  // place du titre sans jamais se lire comme un titre : l'avocat ne voyait pas
  // qu'un document était ouvert. Il coûtait deux fois plus loin, parce que ce
  // libellé est aussi le nom de fichier proposé au téléchargement
  // (`CanvasDownloadButton`) et le nom par défaut de l'enregistrement au
  // dossier (`SaveToCaseDialog`) : la phrase entière partait en `.docx`.
  if (phase === 'idle') return 'Nouveau document';
  if (phase === 'streaming') {
    return activeKind === 'research' ? 'Élaboration du plan en cours…' : 'Rédaction de l\'acte en cours…';
  }
  return activeKind === 'research'
    ? 'Plan prêt à modifier et à exporter'
    : 'Acte généré, prêt à modifier et à exporter';
}

const AUCUN_BLOC: readonly StructuredBlock[] = [];

/** Un bloc porte-t-il du texte ? Un bloc vide n'a pas à ouvrir le canvas. */
function porteDuTexte(bloc: StructuredBlock): boolean {
  if (bloc.kind === 'paragraph') return bloc.runs.some((run) => run.text.trim().length > 0);
  if (bloc.kind === 'table') return bloc.rows.length > 0;
  return false;
}

export function useRedactionStreamingState(
  holderConversationId: string | null,
): RedactionStreamingState {
  // La conversation est lue DIRECTEMENT sur le hub, sans passer par le registre
  // (#1336). L'ancienne traduction conversation → session documentaire faisait
  // dépendre l'affichage d'un enregistrement que la première notification du
  // hub précède : l'abonnement ci-dessous se réveillait sur la conversation du
  // travail pendant que la lecture, elle, cherchait une session pas encore
  // acquise — donc un réveil sans rien à montrer. Les deux emploient désormais
  // la même clé.
  const ecoute = redactionStreamHub.derniereEcoutePourConversation(holderConversationId);
  const jobAffiche = ecoute?.jobId ?? null;
  const kindAffiche: RedactionActiveKind = ecoute?.kind ?? null;

  // Un compteur, pas une copie de l'écoute : le hub est la source, et recopier
  // son état ici le dédoublerait — deux vérités pour une même rédaction.
  const [, redessiner] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(
    () => redactionStreamHub.souscrire((jobId) => {
      // La conversation immutable du travail est la clé : c'est elle que le hub
      // connaît dès son premier signal, et c'est elle que la lecture ci-dessus
      // interroge.
      if (redactionStreamHub.lire(jobId)?.conversationId === holderConversationId) redessiner();
    }),
    [holderConversationId],
  );

  const ecouteAffichee: RedactionEcoute | null = ecoute;

  // ⚠️ Aucune clé de reconstruction de l'éditeur n'est publiée ici (#1337).
  // Un compteur incrémenté PENDANT le rendu à chaque nouveau travail affiché
  // reconstruisait l'éditeur sans que l'époque du runtime le sache — et le
  // second acte ne tenait qu'à lui. C'est désormais l'ADMISSION qui remplace le
  // document d'un travail qui le remplace, par le runtime donc sous une époque
  // neuve, AVANT que ce travail ne soit affiché (`state/canvasAdmission.ts`).

  // Le canvas s'ouvre au premier bloc qui porte du texte, et une seule fois par
  // travail : si l'avocat referme le canvas pendant la rédaction, un bloc de
  // plus ne doit pas le lui rouvrir sous les doigts.
  const ouvertPourRef = React.useRef<string | null>(null);
  const blocs = ecouteAffichee?.blocs;
  React.useEffect(() => {
    if (!jobAffiche || !blocs || ouvertPourRef.current === jobAffiche) return;
    if (!blocs.some(porteDuTexte)) return;
    ouvertPourRef.current = jobAffiche;
    openCanvas(`premier bloc streamé — job ${jobAffiche}`);
    // Dépendre des BLOCS, pas de l'écoute : celle-ci est un objet que le hub
    // mute en place, donc son identité ne change jamais et l'effet ne se
    // rejouerait pas une seule fois. Le tableau de blocs, lui, est remplacé à
    // chaque ajout — c'est très exactement pour cela qu'il l'est.
  }, [jobAffiche, blocs]);

  // Le raisonnement vit dans un store parce que la carte qui l'affiche
  // (`RedactionPlanProgress`) appartient à la frise du chat, un autre arbre.
  // On le REFLÈTE au lieu de l'accumuler : le hub est la seule mémoire du flux,
  // et un reflet ne peut pas perdre les deltas arrivés entre deux rendus.
  React.useEffect(() => {
    if (!jobAffiche) return;
    // Le reflet est indexé par jobId : deux rédactions concurrentes gardent
    // chacune leur raisonnement, sans projection singleton dépendante de l'UI.
    useRedactionThinkingStore.getState().refleter(
      jobAffiche,
      ecouteAffichee?.raisonnement ?? '',
      ecouteAffichee?.contenuCommence ?? false,
    );
  }, [jobAffiche, ecouteAffichee?.raisonnement, ecouteAffichee?.contenuCommence]);

  const phase: RedactionPhase =
    !ecouteAffichee ? 'idle'
    : ecouteAffichee.statut === 'en-cours' ? 'streaming'
    // Une écoute annulée laisse le document dans l'état où l'avocat l'a arrêtée :
    // il est éditable et exportable, donc « terminé » au sens de l'écran.
    : 'done';

  return {
    blocks: ecouteAffichee?.blocs ?? AUCUN_BLOC,
    phase,
    activeKind: kindAffiche,
    headerLabel: computeHeaderLabel(phase, kindAffiche),
    jobId: jobAffiche,
    documentTitle: ecouteAffichee?.documentTitle ?? null,
  };
}
